import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components'
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTrashCan,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons';
import userContext from '../../../hooks/UserContext';
import Api from '../../../utils/Api';
import Loading from '../../../components/Loading';
import ConfirmButton from '../../../components/ConfirmButton';
import Tabs from '../../../components/Tabs';
import MyGifts from './MyGifts';
import MemberPanel from './Members';
import ListPanel from './ListPanel';
import EventPanel from './EventPanel';

const fetchGroup = async id => {
  if (!id) return null;
  const [error, group] = await Api.getGroup(id);
  if (!error) {
    // Ensure events are increasing by date
    group?.events?.sort((a, b) => new Date(a.date) - new Date(b.date));
    return group;
  }
  else {
    toast.error(Api.error(error));
    if (error.response?.status === 404) {
      throw (error);
    }
    return null;
  }
}

const styles = css`
width: 100%;
display: flex;
flex-direction: column;
align-items: center;
.header {
  display: flex;
  flex-wrap: wrap;
  position: relative;
  width: 100%;
  height: 10vh;
  align-content: flex-start;
  justify-content: space-between;
  padding: 10px;
  overflow:hidden;

  > .cover {
    position: absolute;
    top: 0px;
    left: 0px;
    height: 100%;
    width: 100%;
    z-index: -1;
    > img {
       width: 100%;
       height: 100%;
    }
  }

  > .title-row {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    position: relative;

    > .controls {
      position: absolute;
      top: 0px;
      right: 0px;
      display: flex;
      gap: 10px;
      font-size: 0.75em;
    }

    > .name {
      font-size: 1.25em;
      font-weight: bold;
      justify-self: center;
      text-align: center;
      width: 100%;
      padding-bottom: 10px;
    }
  }
  > .description {
      justify-self: center;
      text-align: center;
      width: 100%;
    }
}
.sections {
  display: flex;
  flex-wrap: wrap;
  //max-width: 600px;
  width: 100%;
  padding-top: 10px;
}
`;

const GroupDetails = styled(({ className }) => {
  const { id } = useParams();
  const { user } = useContext(userContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseInt(searchParams.get('tab')) || 0;

  const { isLoading, error, refetch, data: group } = useQuery(['group-details', id], () => fetchGroup(id));

  if (error?.response.status === 404) {
    localStorage.removeItem('last-group');
    navigate('/dashboard');
    return null;
  }

  const isOwner = group?.owner?._id === user?._id;
  const isAdmin = group?.admins?.find(adminId => adminId === user?._id);
  if (group?._id) localStorage.setItem('last-group', group._id);

  const deleteGroup = async () => {
    const [error,] = await Api.deleteGroup(id);
    if (!error) {
      toast.success('Group deleted');
      localStorage.removeItem('last-group');
      navigate(-1);
    } else {
      toast.error(Api.error(error));
    }
  };

  const removeMember = async (userId) => {
    const [error,] = await Api.removeMemberFromGroup({ groupId: group.id, userId });
    if (error) {
      console.error(error);
      toast.error(Api.error(error));
    } else {
      if (user._id === userId) {
        navigate('/', { replace: true })
      } else {
        refetch();
      }
    }
  }

  const addAdmin = async (userId) => {
    const [error,] = await Api.addAdminToGroup({ groupId: group.id, userId });
    if (error) {
      console.error(error);
      toast.error(Api.error(error));
    } else {
      refetch();
    }
  }

  const shareGroup = async () => {
    navigate(`/groups/share/${group.id}`)
  }

  const onActiveTabChange = tab => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <Loading isLoading={isLoading} >
      {group &&
        <div className={className}>
          <div className='header'>
            <div className='cover'>
              {group.image && <img src={group.image} alt='group cover' />}
            </div>
            <div className='title-row'>
              <div className='name'>{group.name}</div>
              {(isOwner || isAdmin) &&
                <div className='controls'>
                  <button className='primary' onClick={shareGroup}>
                    <FontAwesomeIcon icon={faShareNodes} />
                  </button>
                  {isOwner &&
                    <ConfirmButton
                      text={`Do you want to delete the group "${group.name}"?`}
                      confirm='Yes'
                      cancel='No'
                      className='danger'
                      onConfirm={deleteGroup}
                      onCancel={() => { }}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </ConfirmButton>
                  }
                </div>
              }
            </div>
            <div className='description'>
              A group description goes here
            </div>
          </div>
          <Tabs className='sections' activeTab={activeTab} onTabChange={onActiveTabChange}>
            <ListPanel title='Lists'
              group={group}
              onSelectList={list =>
                navigate(`/lists/details/${list._id}?context=group&id=${group._id}`)} />
            <MyGifts title='My Gifts' group={group} />
            <EventPanel
              title='Events'
              group={group} />
            <MemberPanel
              title='Members'
              group={group}
              onRemoveMember={removeMember}
              onAddAdmin={addAdmin} />
          </Tabs>
        </div>
      }
    </Loading>
  )
})`${styles}`;

export default GroupDetails;
